<template>
  <IntercomLayout>
    <template #topbar>
      <SevaTopbar
        :business-name="businessName"
        :user="user"
        :global-user="globalUser"
        @settings="$router.push('/settings')"
        @logout="handleLogout"
      />
    </template>

    <template #sidebar>
      <IntercomSidebar :nav-sections="navSections" />
    </template>

    <div class="beneficiaries-view">
      <!-- Page Header -->
      <div class="page-header">
        <div>
          <h1 class="page-title">{{ $t('beneficiary.title') }}</h1>
          <p class="page-subtitle">{{ $t('beneficiary.subtitle') }}</p>
        </div>
        <IntercomButton variant="primary" @click="showAddModal = true">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <line x1="12" y1="5" x2="12" y2="19" />
            <line x1="5" y1="12" x2="19" y2="12" />
          </svg>
          <span>{{ $t('beneficiary.addBeneficiary') }}</span>
        </IntercomButton>
      </div>

      <!-- Filters -->
      <div class="filters-row">
        <IntercomInput
          v-model="searchQuery"
          :placeholder="$t('search.beneficiariesPlaceholder')"
          type="search"
          class="search-input"
          @input="handleSearch"
        />
        <IntercomSelect
          v-model="filterType"
          :options="typeFilterOptions"
          :placeholder="$t('filters.allTypes')"
          class="filter-select"
          @update:model-value="resetAndFetch"
        />
      </div>

      <!-- Beneficiaries Table -->
      <IntercomCard>
        <IntercomTable
          :columns="columns"
          :data="items"
          :loading="loading"
          hoverable
          clickable
          @row-click="handleRowClick"
        >
          <template #cell-beneficiary_type="{ value }">
            <IntercomBadge v-if="value" :variant="getTypeVariant(value)">
              {{ $t(`beneficiary.types.${value}`) }}
            </IntercomBadge>
            <span v-else class="text-muted">-</span>
          </template>
          <template #cell-category="{ value }">
            <IntercomBadge v-if="value" :variant="getCategoryVariant(value)">
              {{ $t(`beneficiary.categories.${value}`) }}
            </IntercomBadge>
            <span v-else class="text-muted">-</span>
          </template>
          <template #cell-is_active="{ value }">
            <IntercomBadge :variant="value ? 'success' : 'default'">
              {{ value ? $t('common.active') : $t('common.inactive') }}
            </IntercomBadge>
          </template>
        </IntercomTable>
        <p v-if="!loading && items.length === 0" class="empty-text">
          {{ $t('beneficiary.noBeneficiaries') }}
        </p>
      </IntercomCard>

      <IntercomPagination
        :offset="offset"
        :total="total"
        :limit="limit"
        @update:offset="(v) => { offset.value = v; fetch() }"
      />

      <!-- Add/Edit Modal -->
      <IntercomModal
        v-model:show="showAddModal"
        :title="editingBeneficiary ? $t('beneficiary.editBeneficiary') : $t('beneficiary.addBeneficiary')"
      >
        <BeneficiaryForm
          :beneficiary="editingBeneficiary"
          :loading="isSaving"
          @submit="handleSave"
          @cancel="closeModal"
        />
      </IntercomModal>
    </div>
  </IntercomLayout>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useToast } from '@shared/composables/useToast.js'
import { useAuth } from '@shared/composables/useAuth.js'
import { useBusiness } from '@shared/composables/useBusiness.js'
import { usePaginatedList } from '@shared/composables/usePaginatedList.js'
import { useSeva } from '../composables/useSeva.js'
import {
  IntercomLayout,
  IntercomSidebar,
  IntercomButton,
  IntercomCard,
  IntercomTable,
  IntercomBadge,
  IntercomInput,
  IntercomSelect,
  IntercomModal,
  IntercomPagination,
} from '@shared/components/intercom'
import SevaTopbar from '../components/SevaTopbar.vue'
import BeneficiaryForm from '../components/BeneficiaryForm.vue'
import { useNavigation } from '../composables/useNavigation.js'

const { t } = useI18n()
const router = useRouter()
const toast = useToast()
const { user, globalUser, logout } = useAuth()
const { businessName, fetchBusiness } = useBusiness()
const { listBeneficiaries, createBeneficiary, updateBeneficiary } = useSeva()
const { navSections } = useNavigation()

// State
const isSaving = ref(false)
const searchQuery = ref('')
const filterType = ref(null)
const showAddModal = ref(false)
const editingBeneficiary = ref(null)

// Paginated list
const {
  items,
  total,
  loading,
  offset,
  limit,
  fetch,
  resetAndFetch,
} = usePaginatedList(
  (params) => listBeneficiaries({
    ...params,
    ...(searchQuery.value ? { search: searchQuery.value } : {}),
    ...(filterType.value ? { beneficiary_type: filterType.value } : {}),
  }),
  { immediate: false }
)

// Table columns
const columns = [
  { key: 'beneficiary_number', title: t('beneficiary.number'), width: '130px' },
  { key: 'name', title: t('beneficiary.name'), width: '200px' },
  { key: 'phone', title: t('beneficiary.phone'), width: '130px' },
  { key: 'beneficiary_type', title: t('beneficiary.type'), width: '120px' },
  { key: 'category', title: t('beneficiary.category'), width: '120px' },
  { key: 'is_active', title: t('common.active'), width: '100px' },
]

const typeFilterOptions = computed(() => [
  { value: null, label: t('filters.allTypes') },
  { value: 'individual', label: t('beneficiary.types.individual') },
  { value: 'household', label: t('beneficiary.types.household') },
  { value: 'group', label: t('beneficiary.types.group') },
])

const getTypeVariant = (type) => {
  switch (type) {
    case 'individual': return 'info'
    case 'household': return 'success'
    case 'group': return 'warning'
    default: return 'default'
  }
}

const getCategoryVariant = (category) => {
  switch (category) {
    case 'bpl': return 'danger'
    case 'senior_citizen': return 'warning'
    case 'disabled': return 'info'
    case 'student': return 'success'
    case 'general': return 'default'
    default: return 'default'
  }
}

const handleSearch = () => {
  resetAndFetch()
}

const handleRowClick = (beneficiary) => {
  router.push(`/beneficiaries/${beneficiary.id}`)
}

const handleSave = async (formData) => {
  isSaving.value = true
  try {
    const data = { ...formData }
    if (data.phone && data.phone.startsWith('+91')) data.phone = data.phone.slice(3)
    if (!data.phone) data.phone = null

    if (editingBeneficiary.value) {
      await updateBeneficiary(editingBeneficiary.value.id, data)
      toast.success(t('success.beneficiaryUpdated'))
    } else {
      await createBeneficiary(data)
      toast.success(t('success.beneficiaryCreated'))
    }
    closeModal()
    fetch()
  } catch (error) {
    console.error('Failed to save beneficiary:', error)
    toast.error(error.detail || error.message || t('errors.saveFailed'))
  } finally {
    isSaving.value = false
  }
}

const closeModal = () => {
  showAddModal.value = false
  editingBeneficiary.value = null
}

const handleLogout = async () => {
  await logout()
  router.push('/login')
}

onMounted(() => {
  fetchBusiness()
  fetch()
})
</script>

<style scoped>
@import '@shared/styles/intercom-tokens.css';

.beneficiaries-view {
  padding: var(--space-8);
  max-width: var(--content-max-width);
  margin: 0 auto;
}

.page-header {
  margin-bottom: var(--space-6);
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.page-title {
  font-size: var(--font-size-2xl);
  font-weight: var(--font-weight-bold);
  color: var(--color-charcoal);
  margin: 0;
}

.page-subtitle {
  font-size: var(--font-size-base);
  color: var(--color-gray-500);
  margin-top: var(--space-1);
  margin-bottom: 0;
}

.filters-row {
  display: flex;
  gap: var(--space-4);
  margin-bottom: var(--space-6);
  align-items: center;
}

.search-input {
  flex: 1;
  max-width: 300px;
}

.filter-select {
  width: 180px;
}

.empty-text {
  text-align: center;
  color: var(--color-gray-500);
  padding: var(--space-8);
}

@media (max-width: 768px) {
  .beneficiaries-view {
    padding: var(--space-4);
  }

  .page-header {
    flex-direction: column;
    align-items: flex-start;
    gap: var(--space-4);
  }

  .filters-row {
    flex-direction: column;
  }

  .search-input,
  .filter-select {
    max-width: none;
    width: 100%;
  }
}
</style>
